import React, { useEffect, useMemo, useState } from "react";
import {
  FiSearch,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiX,
  FiImage,
  FiFolder,
  FiFolderPlus,
  FiChevronDown,
  FiChevronRight,
  FiTag,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { Trans, useTranslation } from "react-i18next";
import adminAxios from "../../utils/adminAxios";

const emptyCategoryForm = { category_id: null, name: "" };
const emptySubForm = { name: "" };

const imageUrl = (picUrl) =>
  picUrl ? `${process.env.REACT_APP_BASE_URL}${picUrl}` : null;

const Categories = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});

  const [categoryModal, setCategoryModal] = useState({ open: false, mode: "create" });
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);
  const [categoryFile, setCategoryFile] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [subModal, setSubModal] = useState({
    open: false,
    category_id: null,
    parend_id: null,
    contextLabel: "",
  });
  const [subForm, setSubForm] = useState(emptySubForm);

  const filteredCategories = useMemo(
    () =>
      categories.filter((c) =>
        c.name?.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [categories, searchTerm]
  );

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await adminAxios.get("categories/get-all-categories");
      setCategories(response.data.data || []);
    } catch (error) {
      toast.error(
        error.response?.data?.message || t("dashboard.categories.toast.fetchError")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const toggleExpanded = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // ---- Category modal ----
  const openCreateCategory = () => {
    setCategoryForm(emptyCategoryForm);
    setCategoryFile(null);
    setCategoryModal({ open: true, mode: "create" });
  };

  const openEditCategory = (category) => {
    setCategoryForm({ category_id: category.id, name: category.name });
    setCategoryFile(null);
    setCategoryModal({ open: true, mode: "edit" });
  };

  const closeCategoryModal = () => {
    setCategoryModal({ open: false, mode: "create" });
    setCategoryForm(emptyCategoryForm);
    setCategoryFile(null);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (categoryModal.mode === "create" && !categoryFile) {
      toast.error(t("dashboard.categories.toast.imageRequired"));
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("name", categoryForm.name);
      if (categoryFile) formData.append("pic_url", categoryFile);

      if (categoryModal.mode === "create") {
        await adminAxios.post("categories/add-category", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(t("dashboard.categories.toast.createSuccess"));
      } else {
        formData.append("category_id", categoryForm.category_id);
        await adminAxios.patch("categories/update-category", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(t("dashboard.categories.toast.updateSuccess"));
      }
      closeCategoryModal();
      fetchCategories();
    } catch (error) {
      toast.error(
        error.response?.data?.message || t("dashboard.categories.toast.saveError")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteTarget) return;
    try {
      await adminAxios.delete("categories/delete-category", {
        params: { category_id: deleteTarget.id },
      });
      toast.success(t("dashboard.categories.toast.deleteSuccess"));
      setDeleteTarget(null);
      fetchCategories();
    } catch (error) {
      toast.error(
        error.response?.data?.message || t("dashboard.categories.toast.deleteError")
      );
    }
  };

  // ---- Sub-category modal ----
  const openAddSubCategory = (category_id, parend_id, contextLabel) => {
    setSubForm(emptySubForm);
    setSubModal({ open: true, category_id, parend_id, contextLabel });
  };

  const closeSubModal = () => {
    setSubModal({ open: false, category_id: null, parend_id: null, contextLabel: "" });
    setSubForm(emptySubForm);
  };

  const handleSubSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminAxios.post("categories/add-sub-category", {
        name: subForm.name,
        category_id: subModal.category_id,
        ...(subModal.parend_id ? { parend_id: subModal.parend_id } : {}),
      });
      toast.success(t("dashboard.categories.toast.subCreateSuccess"));
      closeSubModal();
      fetchCategories();
    } catch (error) {
      toast.error(
        error.response?.data?.message || t("dashboard.categories.toast.saveError")
      );
    } finally {
      setSaving(false);
    }
  };

  const renderSubCategories = (subCategories, category, depth = 0) => {
    if (!subCategories || subCategories.length === 0) return null;
    return (
      <ul className={depth === 0 ? "mt-3 space-y-2" : "mt-2 ms-6 space-y-2 border-s border-gray-200 dark:border-gray-600 ps-3"}>
        {subCategories.map((sub) => (
          <li key={sub.id}>
            <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
              <div className="flex items-center space-x-2 rtl:space-x-reverse text-sm text-gray-700 dark:text-gray-200">
                <FiTag className="w-3.5 h-3.5 text-gray-400" />
                <span>{sub.name}</span>
              </div>
              <button
                onClick={() =>
                  openAddSubCategory(category.id, sub.id, `${category.name} / ${sub.name}`)
                }
                title={t("dashboard.categories.actions.addSubCategory")}
                className="p-1.5 rounded-md text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30 transition-colors"
              >
                <FiPlus className="w-3.5 h-3.5" />
              </button>
            </div>
            {renderSubCategories(sub.subCategories, category, depth + 1)}
          </li>
        ))}
      </ul>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("dashboard.categories.title")}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t("dashboard.categories.subtitle")}
          </p>
        </div>
        <button
          onClick={openCreateCategory}
          className="mt-4 lg:mt-0 flex items-center justify-center space-x-2 rtl:space-x-reverse px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all"
        >
          <FiPlus className="w-4 h-4" />
          <span>{t("dashboard.categories.addButton")}</span>
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <FiSearch className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          placeholder={t("dashboard.categories.search.placeholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 rtl:pl-4 rtl:pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
        />
      </div>

      {/* Categories grid */}
      {filteredCategories.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 text-center py-12">
          <div className="text-gray-400 dark:text-gray-500 text-6xl mb-4">🗂️</div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t("dashboard.categories.emptyState.title")}
          </h3>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredCategories.map((category) => {
            const isOpen = !!expanded[category.id];
            const url = imageUrl(category.pic_url);
            return (
              <div
                key={category.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden"
              >
                <div className="flex items-center p-4 space-x-4 rtl:space-x-reverse">
                  <div className="w-14 h-14 rounded-lg bg-gray-100 dark:bg-gray-700 overflow-hidden flex items-center justify-center flex-shrink-0">
                    {url ? (
                      <img src={url} alt={category.name} className="w-full h-full object-cover" />
                    ) : (
                      <FiImage className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                      {category.name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t("dashboard.categories.card.subCategoriesCount", {
                        count: category.subCategories?.length || 0,
                      })}
                    </p>
                  </div>
                  <div className="flex items-center space-x-1 rtl:space-x-reverse flex-shrink-0">
                    <button
                      onClick={() => openEditCategory(category)}
                      title={t("dashboard.categories.actions.edit")}
                      className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30 transition-colors"
                    >
                      <FiEdit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(category)}
                      title={t("dashboard.categories.actions.delete")}
                      className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30 transition-colors"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => toggleExpanded(category.id)}
                      className="flex items-center space-x-1 rtl:space-x-reverse text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                    >
                      {isOpen ? (
                        <FiChevronDown className="w-4 h-4" />
                      ) : (
                        <FiChevronRight className="w-4 h-4" />
                      )}
                      <FiFolder className="w-4 h-4" />
                      <span>{t("dashboard.categories.card.subCategoriesLabel")}</span>
                    </button>
                    <button
                      onClick={() => openAddSubCategory(category.id, null, category.name)}
                      title={t("dashboard.categories.actions.addSubCategory")}
                      className="flex items-center space-x-1 rtl:space-x-reverse text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                    >
                      <FiFolderPlus className="w-4 h-4" />
                      <span>{t("dashboard.categories.actions.add")}</span>
                    </button>
                  </div>

                  {isOpen &&
                    (category.subCategories?.length ? (
                      renderSubCategories(category.subCategories, category)
                    ) : (
                      <p className="mt-3 text-sm text-gray-400 dark:text-gray-500">
                        {t("dashboard.categories.card.noSubCategories")}
                      </p>
                    ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit Category Modal */}
      {categoryModal.open && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={closeCategoryModal}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {categoryModal.mode === "create"
                    ? t("dashboard.categories.modal.createTitle")
                    : t("dashboard.categories.modal.editTitle")}
                </h3>
                <button
                  onClick={closeCategoryModal}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleCategorySubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t("dashboard.categories.modal.fields.name.label")}
                  </label>
                  <input
                    type="text"
                    required
                    value={categoryForm.name}
                    onChange={(e) =>
                      setCategoryForm({ ...categoryForm, name: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t("dashboard.categories.modal.fields.image.label")}{" "}
                    {categoryModal.mode === "edit" && (
                      <span className="text-gray-400 text-xs">
                        {t("dashboard.categories.modal.fields.image.editHint")}
                      </span>
                    )}
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setCategoryFile(e.target.files?.[0] || null)}
                    className="w-full text-sm text-gray-600 dark:text-gray-300 file:mr-4 rtl:file:mr-0 rtl:file:ml-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 dark:file:bg-blue-900/30 dark:file:text-blue-300 hover:file:bg-blue-100"
                  />
                  {(categoryFile || categoryForm.category_id) && (
                    <div className="mt-3 w-20 h-20 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                      {categoryFile ? (
                        <img
                          src={URL.createObjectURL(categoryFile)}
                          alt="preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <FiImage className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 rtl:space-x-reverse pt-2">
                  <button
                    type="button"
                    onClick={closeCategoryModal}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    {t("dashboard.categories.modal.buttons.cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-50"
                  >
                    {saving
                      ? t("dashboard.categories.modal.buttons.saving")
                      : t("dashboard.categories.modal.buttons.save")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Sub-category Modal */}
      {subModal.open && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={closeSubModal}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {t("dashboard.categories.subModal.title")}
                </h3>
                <button
                  onClick={closeSubModal}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {t("dashboard.categories.subModal.context")}{" "}
                <span className="font-medium text-gray-700 dark:text-gray-200">
                  {subModal.contextLabel}
                </span>
              </p>

              <form onSubmit={handleSubSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t("dashboard.categories.subModal.fields.name.label")}
                  </label>
                  <input
                    type="text"
                    required
                    value={subForm.name}
                    onChange={(e) => setSubForm({ name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>

                <div className="flex justify-end space-x-3 rtl:space-x-reverse pt-2">
                  <button
                    type="button"
                    onClick={closeSubModal}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    {t("dashboard.categories.modal.buttons.cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-50"
                  >
                    {saving
                      ? t("dashboard.categories.modal.buttons.saving")
                      : t("dashboard.categories.modal.buttons.save")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setDeleteTarget(null)}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                {t("dashboard.categories.deleteModal.title")}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                <Trans
                  i18nKey="dashboard.categories.deleteModal.confirmText"
                  values={{ name: deleteTarget.name }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <div className="flex justify-end space-x-3 rtl:space-x-reverse">
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {t("dashboard.categories.deleteModal.cancel")}
                </button>
                <button
                  onClick={handleDeleteCategory}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  {t("dashboard.categories.deleteModal.confirmButton")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;
